const GLOBAL_CSS_KEY = 'eb-global-custom-css'

export default function elementorCustomCss(editor) {
  let globalCss = ''
  try {
    globalCss = localStorage.getItem(GLOBAL_CSS_KEY) || ''
  } catch (e) { /* ignore */ }

  const componentCss = {}

  editor.on('component:selected', (component) => {
    addCustomCssTrait(component)
  })

  function addCustomCssTrait(component) {
    const existingTrait = component.getTrait('data-custom-css')
    if (existingTrait) return

    component.addTrait({
      type: 'text',
      name: 'data-custom-css',
      label: 'Custom CSS',
      placeholder: 'selector { color: red; }\nselector:hover { opacity: 0.8; }',
      changeProp: true,
    })

    component.on('change:attributes:data-custom-css', () => {
      updateComponentCss(component)
    })

    const savedCss = component.getAttributes()['data-custom-css']
    if (savedCss) {
      updateComponentCss(component)
    }
  }

  function getComponentSelector(component) {
    const classes = component.getClasses()
    if (classes.length) return '.' + classes[0]
    const id = component.getId()
    if (id) return '#' + id
    return null
  }

  function updateComponentCss(component) {
    const customCss = component.getAttributes()['data-custom-css'] || ''
    const selector = getComponentSelector(component)
    if (!selector) return

    const resolved = customCss.replace(/selector/g, selector)
    componentCss[component.cid] = resolved

    injectCanvasCss()
  }

  function getAllCustomCss() {
    let css = ''
    if (globalCss) css += '/* Global Custom CSS */\n' + globalCss + '\n'
    const perEl = Object.values(componentCss).filter(Boolean).join('\n')
    if (perEl) css += '/* Per-Element Custom CSS */\n' + perEl + '\n'
    return css
  }

  function injectCanvasCss() {
    const frame = editor.Canvas.getFrameEl()
    if (!frame) return
    const doc = frame.contentDocument
    if (!doc) return

    let styleEl = doc.getElementById('eb-custom-css')
    if (!styleEl) {
      styleEl = doc.createElement('style')
      styleEl.id = 'eb-custom-css'
      doc.head.appendChild(styleEl)
    }
    styleEl.textContent = getAllCustomCss()
  }

  editor.Commands.add('open-global-css', {
    run(editor) {
      const modal = editor.Modal
      modal.setTitle('Global Custom CSS')
      modal.setContent(`
        <div style="height:450px;display:flex;flex-direction:column;">
          <p style="font-size:12px;color:#8892b0;margin:0 0 10px;">Add custom CSS that applies globally to your page. Use standard CSS selectors.</p>
          <textarea id="eb-global-css-editor" style="flex:1;font-family:'SF Mono',Consolas,Monaco,monospace;font-size:13px;padding:14px;border:1px solid #3c3c4a;border-radius:8px;background:#0d1117;color:#c9d1d9;resize:none;line-height:1.6;tab-size:2;">${escapeHtml(globalCss)}</textarea>
          <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:12px;">
            <button id="eb-gcss-cancel" style="padding:8px 20px;background:#2c2c3a;color:#adb5bd;border:1px solid #3c3c4a;border-radius:6px;cursor:pointer;font-weight:500;">Cancel</button>
            <button id="eb-gcss-apply" style="padding:8px 20px;background:#667eea;color:white;border:none;border-radius:6px;cursor:pointer;font-weight:600;">Apply</button>
          </div>
        </div>
      `)
      modal.open()

      const textarea = document.getElementById('eb-global-css-editor')
      if (textarea) {
        textarea.addEventListener('keydown', (e) => {
          if (e.key === 'Tab') {
            e.preventDefault()
            const start = textarea.selectionStart
            const end = textarea.selectionEnd
            textarea.value = textarea.value.substring(0, start) + '  ' + textarea.value.substring(end)
            textarea.selectionStart = textarea.selectionEnd = start + 2
          }
        })
      }

      const applyBtn = document.getElementById('eb-gcss-apply')
      if (applyBtn) {
        applyBtn.onclick = () => {
          globalCss = document.getElementById('eb-global-css-editor').value
          try { localStorage.setItem(GLOBAL_CSS_KEY, globalCss) } catch (e) { /* ignore */ }
          injectCanvasCss()
          modal.close()
        }
      }

      const cancelBtn = document.getElementById('eb-gcss-cancel')
      if (cancelBtn) {
        cancelBtn.onclick = () => modal.close()
      }
    },
  })

  function escapeHtml(str) {
    return (str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  editor.on('load', () => {
    injectCanvasCss()

    const wrapper = editor.DomComponents.getWrapper()
    if (wrapper) {
      wrapper.components().forEach(function walk(comp) {
        const css = comp.getAttributes()['data-custom-css']
        if (css) updateComponentCss(comp)
        comp.components().forEach(walk)
      })
    }
  })

  editor.getCustomCss = function () {
    return getAllCustomCss()
  }

  editor.DomComponents.addType('eb-grid-container', {
    isComponent(el) {
      return el.getAttribute && el.getAttribute('data-gjs-type') === 'eb-grid-container'
    },
    model: {
      defaults: {
        name: 'Grid Container',
        tagName: 'div',
        droppable: true,
        attributes: { 'data-gjs-type': 'eb-grid-container' },
        style: {
          'display': 'grid',
          'grid-template-columns': 'repeat(3, 1fr)',
          'gap': '20px',
          'padding': '20px',
        },
        traits: [
          { type: 'text', name: 'data-grid-cols', label: 'Grid Columns', placeholder: 'repeat(3, 1fr)', changeProp: true },
          { type: 'text', name: 'data-grid-rows', label: 'Grid Rows', placeholder: 'auto' },
          { type: 'text', name: 'data-grid-gap', label: 'Gap', placeholder: '20px', changeProp: true },
        ],
      },
      init() {
        this.on('change:attributes:data-grid-cols', this.updateGrid)
        this.on('change:attributes:data-grid-gap', this.updateGrid)
      },
      updateGrid() {
        const cols = this.getAttributes()['data-grid-cols'] || 'repeat(3, 1fr)'
        const rows = this.getAttributes()['data-grid-rows'] || 'auto'
        const gap = this.getAttributes()['data-grid-gap'] || '20px'
        this.setStyle({
          ...this.getStyle(),
          'grid-template-columns': cols,
          'grid-template-rows': rows,
          'gap': gap,
        })
      },
    },
  })

  editor.BlockManager.add('eb-grid-container', {
    label: 'Grid Container',
    category: 'Layout',
    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3,11H11V3H3M3,21H11V13H3M13,21H21V13H13M13,3V11H21V3"/></svg>',
    content: { type: 'eb-grid-container' },
  })

  editor.BlockManager.add('eb-grid-2x2', {
    label: 'Grid 2×2',
    category: 'Layout',
    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3,11H11V3H3M3,21H11V13H3M13,21H21V13H13M13,3V11H21V3"/></svg>',
    content: `<div data-gjs-type="eb-grid-container" style="display:grid;grid-template-columns:1fr 1fr;gap:20px;padding:20px;">
      <div style="background:#f8f9fa;padding:30px;border-radius:8px;min-height:100px;"></div>
      <div style="background:#f8f9fa;padding:30px;border-radius:8px;min-height:100px;"></div>
      <div style="background:#f8f9fa;padding:30px;border-radius:8px;min-height:100px;"></div>
      <div style="background:#f8f9fa;padding:30px;border-radius:8px;min-height:100px;"></div>
    </div>`,
  })

  editor.BlockManager.add('eb-grid-3x1', {
    label: 'Grid 3×1',
    category: 'Layout',
    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2,2H8V8H2V2M9,2H15V8H9V2M16,2H22V8H16V2Z"/></svg>',
    content: `<div data-gjs-type="eb-grid-container" style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px;padding:20px;">
      <div style="background:#f8f9fa;padding:30px;border-radius:8px;min-height:100px;"></div>
      <div style="background:#f8f9fa;padding:30px;border-radius:8px;min-height:100px;"></div>
      <div style="background:#f8f9fa;padding:30px;border-radius:8px;min-height:100px;"></div>
    </div>`,
  })
}
